import React, { useState, useEffect } from "react";
import { Button, Card, Badge, Alert, Modal } from "react-bootstrap";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Drawer } from "../theme/components";
import { API_URL } from "../config/api";
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "../components/Breadcrumb";
import PageHeader from "../components/common/PageHeader";
import ResponsiblesService from "../services/api/responsibles";
import ResponsibleRegistrationForm from "../components/responsibles/ResponsibleRegistrationForm";

const ResponsiblesPage = () => {
  const navigate = useNavigate();
  const [responsibles, setResponsibles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [responsibleToDelete, setResponsibleToDelete] = useState(null);
  const [bankAccountsCount, setBankAccountsCount] = useState(0); // ✅ Contador de contas bancárias
  const [showSuccessModal, setShowSuccessModal] = useState(false); // ✅ Modal de sucesso
  const [deletionResult, setDeletionResult] = useState(null); // ✅ Resultado da exclusão
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [selectedResponsible, setSelectedResponsible] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [showNewResponsibleModal, setShowNewResponsibleModal] = useState(false);
  const [showDetailsDrawer, setShowDetailsDrawer] = useState(false); // ✅ Drawer de detalhes

  // Carregar responsáveis
  const loadResponsibles = async () => {
    try {
      setLoading(true);
      const response = await ResponsiblesService.getResponsibles();
      setResponsibles(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar responsáveis:', error);
      toast.error('Erro ao carregar responsáveis');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadResponsibles();
  }, []);

  // Confirmar exclusão
  const handleDeleteClick = async (responsible) => {
    setResponsibleToDelete(responsible);
    
    // ✅ Buscar quantas contas bancárias estão vinculadas
    try {
      const response = await fetch(`${API_URL}/api/bank-accounts?responsible_id=${responsible.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      setBankAccountsCount(data.data?.length || 0);
    } catch (error) {
      console.error('Erro ao buscar contas bancárias:', error);
      setBankAccountsCount(0);
    }
    
    setShowDeleteModal(true);
  };

  // Excluir responsável + contas bancárias
  const handleDeleteConfirm = async () => {
    try {
      const result = await ResponsiblesService.deleteResponsible(responsibleToDelete.id);
      
      if (result.success) {
        // ✅ Salvar resultado para exibir no modal de sucesso
        setDeletionResult({
          responsibleName: responsibleToDelete.company_name || responsibleToDelete.trading_name || responsibleToDelete.admin_name || 'Responsável',
          deletedBankAccounts: result.deletedBankAccounts || 0
        });
        
        // ✅ Fechar modal de confirmação e abrir modal de sucesso
        setShowDeleteModal(false);
        setShowSuccessModal(true);
        setResponsibleToDelete(null);
        loadResponsibles();
      } else {
        // Erro retornado pelo backend
        toast.error(result.message || 'Erro ao excluir responsável');
      }
    } catch (error) {
      console.error('Erro ao excluir responsável:', error);
      
      // Verificar se há mensagem de erro da API
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao excluir responsável';
      toast.error(errorMessage);
    }
  };

  // Ver documentos do responsável
  const handleViewDocuments = async (responsible) => {
    try {
      setSelectedResponsible(responsible);
      setShowDocumentsModal(true);
      const response = await ResponsiblesService.getDocuments(responsible.id);
      if (response.success) {
        setDocuments(response.data || []);
      } else {
        toast.error('Erro ao carregar documentos');
        setDocuments([]);
      }
    } catch (error) {
      console.error('Erro ao carregar documentos:', error);
      toast.error('Erro ao carregar documentos');
      setDocuments([]);
    }
  };

  // Fechar modal de documentos
  const handleCloseDocumentsModal = () => {
    setShowDocumentsModal(false);
    setSelectedResponsible(null);
    setDocuments([]);
  };

  // Ver detalhes do responsável
  const handleViewDetails = async (responsible) => {
    try {
      setSelectedResponsible(responsible);
      setShowDetailsDrawer(true);
      
      // Carregar documentos do responsável
      const response = await ResponsiblesService.getDocuments(responsible.id);
      if (response.success) {
        console.log('📄 Documentos carregados:', response.data);
        setDocuments(response.data || []);
      } else {
        setDocuments([]);
      }
    } catch (error) {
      console.error('Erro ao carregar documentos:', error);
      setDocuments([]);
    }
  };

  // Função auxiliar para verificar se é imagem
  const isImageFile = (filename) => {
    if (!filename) return false;
    const ext = filename.toLowerCase().split('.').pop();
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(ext);
  };

  // Função para construir URL completa do documento
  /**
   * 🔗 OBTER URL DO DOCUMENTO
   * O backend já retorna as URLs transformadas para usar o proxy local (/api/images/)
   * Então apenas retornamos o campo 'url' ou 'file_url' diretamente
   */
  const getDocumentUrl = (doc) => {
    if (!doc) return null;
    
    // Backend já retorna URL transformada, basta usar diretamente
    const documentUrl = doc.url || doc.file_url;
    
    if (documentUrl) {
      console.log('🔗 [getDocumentUrl] URL do documento:', documentUrl);
      return documentUrl;
    }
    
    console.warn('⚠️ [getDocumentUrl] Documento sem URL:', doc);
    return null;
  };

  // Função para formatar data
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Renderizar item de responsável
  const renderResponsibleItem = (responsible) => {
    // 🏢 Verificar se é cadastro incompleto
    const isIncomplete = responsible.is_complete === false || responsible.status === 'incomplete';
    
    return (
      <div key={responsible.id} className="border-bottom py-5 px-2 mb-4">
        <div className="d-flex align-items-start">
          {/* Avatar/Ícone */}
          <div 
            className="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
            style={{ 
              width: '50px', 
              height: '50px', 
              backgroundColor: isIncomplete ? '#fff3cd' : '#e9ecef'
            }}
          >
            <Icon 
              icon={isIncomplete ? "mdi:alert-circle" : "mdi:domain"} 
              className={isIncomplete ? "text-warning" : "text-primary"} 
              size="24" 
            />
          </div>

          {/* Informações principais */}
          <div className="flex-grow-1">
            <div className="d-flex justify-content-between align-items-start mb-2">
              <div>
                <div className="d-flex align-items-center gap-2 mb-1">
                  <h6 className="mb-0 fw-bold">
                    {isIncomplete ? 
                      (responsible.admin_name || 'Responsável') : 
                      (responsible.company_name || responsible.trading_name || responsible.admin_name || 'Responsável')
                    }
                  </h6>
                  {isIncomplete && (
                    <Badge bg="warning" text="dark" className="small">
                      <Icon icon="mdi:clock-alert" className="me-1" size="10" />
                      Cadastro Incompleto
                    </Badge>
                  )}
                  {!isIncomplete && responsible.is_main_responsible && (
                    <Badge bg="primary" className="small">
                      <Icon icon="mdi:star" className="me-1" size="10" />
                      Principal
                    </Badge>
                  )}
                </div>
                {!isIncomplete && (
                  <>
                    <p className="text-muted mb-1 small">
                      {/* ✅ Detectar se é CPF ou CNPJ baseado no tamanho */}
                      {(() => {
                        const cleanDoc = (responsible.responsible_cpf || '').replace(/\D/g, '');
                        const isPJ = cleanDoc.length === 14;
                        const isPF = cleanDoc.length === 11;
                        
                        if (isPJ) {
                          return `CNPJ: ${ResponsiblesService.formatCNPJ(responsible.responsible_cpf)}`;
                        } else if (isPF) {
                          return `CPF: ${ResponsiblesService.formatCPF(responsible.responsible_cpf)}`;
                        } else {
                          return 'Documento: Não informado';
                        }
                      })()}
                    </p>
                    <p className="text-muted mb-0 small">
                      {(() => {
                        const cleanDoc = (responsible.responsible_cpf || '').replace(/\D/g, '');
                        const isPJ = cleanDoc.length === 14;
                        return isPJ ? `Administrador: ${responsible.admin_name}` : `Nome: ${responsible.admin_name || 'Não informado'}`;
                      })()}
                    </p>
                  </>
                )}
                {isIncomplete && (
                  <p className="text-muted mb-0 small">
                    Complete os dados para liberar todas as funcionalidades
                  </p>
                )}
              </div>
              
              {/* Status */}
              {!isIncomplete && (
                <Badge 
                  className={ResponsiblesService.getStatusBadgeClass(responsible.status)}
                >
                  {ResponsiblesService.getStatusText(responsible.status)}
                </Badge>
              )}
            </div>

          {/* Informações adicionais */}
          <div className="row g-2 mb-3">
            {/* ✅ Mostrar telefone APENAS se informado */}
            {responsible.responsible_phone && (
              <div className="col-md-4">
                <small className="text-muted d-flex align-items-center">
                  <Icon icon="mdi:phone" className="me-1" size="14" />
                  {ResponsiblesService.formatPhone(responsible.responsible_phone)}
                </small>
              </div>
            )}
            {/* ✅ Mostrar endereço APENAS se informado */}
            {responsible.address_city && responsible.address_state && (
              <div className="col-md-4">
                <small className="text-muted d-flex align-items-center">
                  <Icon icon="mdi:map-marker" className="me-1" size="14" />
                  {responsible.address_city}/{responsible.address_state}
                </small>
              </div>
            )}
            {/* ✅ Mostrar documentos APENAS se não estiver aprovado */}
            {responsible.status !== 'approved' && (
              <div className="col-md-4">
                <small className="text-muted d-flex align-items-center">
                  <Icon icon="mdi:file-document-multiple" className="me-1" size="14" />
                  Documentos: {responsible.approved_documents || 0}/{responsible.documents_count || 0} aprovados
                </small>
              </div>
            )}
          </div>

          {/* 📢 Aviso de Aguardando Aprovação */}
          {!isIncomplete && (responsible.status === 'pending' || responsible.status === 'pending_validation' || responsible.status === 'pending_approval') && (
            <Alert variant="info" className="mb-3 py-2 small d-flex align-items-center">
              <Icon icon="mdi:clock-outline" className="me-2" size="18" />
              <div>
                <strong>Aguardando Aprovação</strong>
                <div className="mt-1">
                  Seus dados estão em análise. O prazo é de até <strong>2 dias úteis</strong> para aprovação.
                </div>
              </div>
            </Alert>
          )}

          {responsible.rejection_reason && (
            <Alert variant="danger" className="mb-3 py-2 small">
              <Icon icon="mdi:alert-circle" className="me-2" size="14" />
              <strong>Rejeição:</strong> {responsible.rejection_reason}
            </Alert>
          )}

          {/* Botões de Ação */}
          <div className="d-flex gap-2">
            {/* 🏢 BOTÃO FINALIZAR CADASTRO (se incompleto) */}
            {isIncomplete ? (
              <Button
                variant="warning"
                size="sm"
                onClick={() => navigate(`/responsibles/complete/${responsible.id}`)}
                style={{ fontWeight: '600' }}
              >
                <Icon icon="mdi:check-circle" className="me-1" size="14" />
                Finalizar Cadastro
              </Button>
            ) : (
              <>
                {/* Botão Editar - Lógica de bloqueio */}
                {responsible.status === 'approved' ? (
                  <div className="position-relative">
                    <Button
                      variant="outline-secondary icon-card"
                      size="sm"
                      disabled
                      style={{ cursor: 'not-allowed', opacity: 0.6 }}
                      title="Responsáveis aprovados não podem ser editados. Para fazer alterações, exclua este e cadastre um novo."
                    >
                      <Icon icon="mdi:lock" className="me-1" size="14" />
                      Aprovado
                    </Button>
                  </div>
                ) : (responsible.status === 'pending' || responsible.status === 'pending_validation' || responsible.status === 'pending_approval') ? (
                  <div className="position-relative">
                    <Button
                      variant="outline-warning icon-card"
                      size="sm"
                      disabled
                      style={{ cursor: 'not-allowed', opacity: 0.6 }}
                      title="Responsável em análise. Não é possível editar enquanto está sendo analisado. Aguarde a aprovação ou rejeição, ou exclua e cadastre novamente."
                    >
                      <Icon icon="mdi:clock-alert" className="me-1" size="14" />
                      Em Análise
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="outline-primary icon-card"
                    size="sm"
                    onClick={() => navigate(`/responsibles/complete/${responsible.id}`)}
                    title={responsible.status === 'rejected' ? `Rejeitado: ${responsible.rejection_reason || 'Sem motivo especificado'}. Clique para corrigir e reenviar.` : 'Editar responsável'}
                  >
                    <Icon icon="mdi:pencil" className="me-1" size="14" />
                    {responsible.status === 'rejected' ? 'Corrigir' : 'Editar'}
                  </Button>
                )}
              </>
            )}
            {/* ✅ Botão Ver Dados - só aparece se não for incompleto */}
            {!isIncomplete && (
              <Button
                variant="outline-success icon-card"
                size="sm"
                onClick={() => handleViewDetails(responsible)}
              >
                <Icon icon="mdi:eye" className="me-1" size="14" />
                Ver Dados
              </Button>
            )}
            {/* Botão Ver Documentos - só aparece se não for incompleto */}
            {!isIncomplete && (
              <Button
                variant="outline-info icon-card"
                size="sm"
                onClick={() => handleViewDocuments(responsible)}
              >
                <Icon icon="mdi:file-document-multiple" className="me-1" size="14" />
                Ver Documentos
              </Button>
            )}
            <Button
              variant="outline-danger icon-card"
              size="sm"
              onClick={() => handleDeleteClick(responsible)}
            >
              <Icon icon="mdi:delete" className="me-1" size="14" />
              Excluir
            </Button>
          </div>
        </div>
      </div>
    </div>
    );
  };

  if (loading) {
    return (
      <MasterLayout>
        <Breadcrumb 
          title="Responsáveis" 
          items={[
            { label: 'Configurações', path: '/settings' }
          ]}
        />
        <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Carregando...</span>
            </div>
            <p className="mt-3">Carregando responsáveis...</p>
          </div>
        </div>
      </MasterLayout>
    );
  }

  return (
    <MasterLayout>
        <Breadcrumb 
          title="Responsáveis" 
          items={[
            { label: 'Dashboard', path: '/Dashboard' },
            { label: 'Financeiro', path: '/banks' }
          ]}
        />
        
        <div className="container-fluid px-4">
          {/* Header Section */}
          <PageHeader
            icon="mdi:account-tie"
            title="Responsáveis"
            subtitle="Gerencie os responsáveis legais da sua conta. É necessário ter pelo menos um responsável aprovado para cadastrar produtos e vincular contas bancárias."
          >
            <button 
              onClick={() => setShowNewResponsibleModal(true)}
              className="btn btn-primary d-flex align-items-center gap-2"
              style={{
                height: '38px',
                fontSize: '14px',
                borderRadius: '6px',
                paddingLeft: '16px',
                paddingRight: '16px'
              }}
            >
              <Icon icon="mdi:plus" />
              Novo Responsável
            </button>
          </PageHeader>

          {/* Alerta se não tiver responsáveis aprovados */}
          {responsibles.length > 0 && !responsibles.some(r => r.status === 'approved') && (
            <Alert variant="warning" className="mb-4 border-0 shadow-sm" style={{ borderRadius: '0.75rem' }}>
              <div className="d-flex align-items-center">
                <Icon icon="mdi:alert-circle" className="me-3 text-warning" size="24" />
                <div>
                  <strong>Atenção!</strong> Você não possui nenhum responsável aprovado. 
                  <br />
                  <small className="text-muted">Para cadastrar produtos e vincular contas bancárias, é necessário ter pelo menos um responsável com status "Aprovado".</small>
                </div>
              </div>
            </Alert>
          )}

          {/* Lista de responsáveis */}
          {responsibles.length === 0 ? (
            <div className="bg-white border rounded text-center py-5 px-4 mt-4">
              <Icon icon="mdi:account-group" size="80" className="text-muted mb-4" />
              <h4 className="text-dark mb-3">Nenhum responsável cadastrado</h4>
              <p className="text-muted mb-4" style={{ maxWidth: '500px', margin: '0 auto' }}>
                Adicione pelo menos um responsável para poder cadastrar produtos e vincular contas bancárias na plataforma.
              </p>
              <p className="text-muted">
                <small>Use o botão "Novo Responsável" no topo da página para começar.</small>
              </p>
            </div>
          ) : (
            <div className="bg-white border rounded p-3 mt-4">
              {responsibles.map(renderResponsibleItem)}
            </div>
          )}
        </div>

        {/* Modal de cadastro de responsável (NOVO - Componente Moderno) */}
        <Modal 
          show={showNewResponsibleModal} 
          onHide={() => setShowNewResponsibleModal(false)}
          size="xl"
          centered
          style={{ zIndex: 9999 }}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <Icon icon="mdi:account-plus" className="me-2 text-primary" />
              Novo Responsável
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: '75vh', overflowY: 'auto', padding: 0 }}>
            <ResponsibleRegistrationForm
              onSuccess={() => {
                setShowNewResponsibleModal(false);
                loadResponsibles();
              }}
              onCancel={() => setShowNewResponsibleModal(false)}
            />
          </Modal.Body>
        </Modal>


        {/* Modal de confirmação de exclusão */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title className="text-danger">
              <Icon icon="mdi:delete" className="me-2" />
              Excluir Responsável
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Alert variant="danger">
              <Icon icon="mdi:alert-circle" className="me-2" />
              <strong>Atenção!</strong> Esta ação não pode ser desfeita.
            </Alert>
            <p>
              Tem certeza que deseja excluir o responsável{' '}
              <strong>{responsibleToDelete?.company_name || responsibleToDelete?.trading_name || responsibleToDelete?.admin_name || 'este responsável'}</strong>?
            </p>
            
            {/* ✅ Avisar sobre contas bancárias vinculadas */}
            {bankAccountsCount > 0 && (
              <Alert variant="warning" className="mt-3">
                <Icon icon="mdi:bank" className="me-2" />
                <strong>Atenção:</strong> Este responsável possui{' '}
                <strong>{bankAccountsCount} conta(s) bancária(s)</strong> vinculada(s).{' '}
                Todas as contas bancárias também serão excluídas permanentemente.
              </Alert>
            )}
            
            <p className="text-muted mt-3">
              <strong>Também serão removidos:</strong>
            </p>
            <ul className="text-muted">
              <li>Todos os documentos associados</li>
              {bankAccountsCount > 0 && (
                <li><strong>{bankAccountsCount} conta(s) bancária(s)</strong></li>
              )}
            </ul>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDeleteConfirm}>
              <Icon icon="mdi:delete" className="me-2" />
              Excluir Tudo
            </Button>
          </Modal.Footer>
        </Modal>

        {/* ✅ Modal de sucesso após exclusão */}
        <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} centered>
          <Modal.Header closeButton className="bg-success text-white">
            <Modal.Title>
              <Icon icon="mdi:check-circle" className="me-2" />
              Exclusão Concluída
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="text-center py-3">
              <Icon icon="mdi:check-circle" style={{ fontSize: '64px', color: '#28a745' }} />
              <h5 className="mt-3">Responsável excluído com sucesso!</h5>
              <p className="text-muted mt-2">
                <strong>{deletionResult?.responsibleName}</strong> foi removido do sistema.
              </p>
              
              {deletionResult?.deletedBankAccounts > 0 && (
                <Alert variant="info" className="mt-3">
                  <Icon icon="mdi:information" className="me-2" />
                  <strong>{deletionResult.deletedBankAccounts} conta(s) bancária(s)</strong> também foram excluídas.
                </Alert>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="success" onClick={() => setShowSuccessModal(false)}>
              <Icon icon="mdi:check" className="me-2" />
              Entendi
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Modal de Documentos */}
        <Modal show={showDocumentsModal} onHide={handleCloseDocumentsModal} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title className="d-flex align-items-center">
              <Icon icon="mdi:file-document-multiple" className="me-2 text-primary" />
              Documentos do Responsável
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedResponsible && (
              <div className="mb-3">
                <h6 className="fw-bold">{selectedResponsible.company_name || selectedResponsible.trading_name || selectedResponsible.admin_name}</h6>
                <p className="text-muted small mb-0">
                  {(() => {
                    const cleanDoc = (selectedResponsible.responsible_cpf || '').replace(/\D/g, '');
                    const isPJ = cleanDoc.length === 14;
                    const isPF = cleanDoc.length === 11;
                    
                    if (isPJ) {
                      return `CNPJ: ${ResponsiblesService.formatCNPJ(selectedResponsible.responsible_cpf)}`;
                    } else if (isPF) {
                      return `CPF: ${ResponsiblesService.formatCPF(selectedResponsible.responsible_cpf)}`;
                    } else {
                      return 'Documento: N/A';
                    }
                  })()}
                </p>
              </div>
            )}

            {documents.length === 0 ? (
              <div className="text-center py-5">
                <Icon icon="mdi:file-document-off" size="60" className="text-muted mb-3" />
                <p className="text-muted">Nenhum documento enviado</p>
              </div>
            ) : (
              <div className="list-group">
                {documents.map((doc) => (
                  <div key={doc.id} className="list-group-item d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-3 flex-grow-1">
                      <Icon 
                        icon={doc.status === 'approved' ? 'mdi:file-check' : doc.status === 'rejected' ? 'mdi:file-alert' : 'mdi:file-clock'} 
                        size="28" 
                        className={doc.status === 'approved' ? 'text-success' : doc.status === 'rejected' ? 'text-danger' : 'text-warning'}
                      />
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <strong>{doc.document_type.toUpperCase()}</strong>
                          <Badge bg={doc.status === 'approved' ? 'success' : doc.status === 'rejected' ? 'danger' : 'warning'}>
                            {doc.status === 'approved' ? 'Aprovado' : doc.status === 'rejected' ? 'Rejeitado' : 'Pendente'}
                          </Badge>
                        </div>
                        <p className="text-muted small mb-0">{doc.file_name}</p>
                        {doc.rejection_reason && (
                          <p className="text-danger small mb-0 mt-1">
                            <strong>Motivo:</strong> {doc.rejection_reason}
                          </p>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => {
                        const docUrl = getDocumentUrl(doc);
                        if (docUrl) {
                          window.open(docUrl, '_blank');
                        }
                      }}
                    >
                      <Icon icon="mdi:eye" className="me-1" size="14" />
                      Visualizar
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="light" 
              onClick={handleCloseDocumentsModal}
              style={{
                borderRadius: '8px',
                padding: '10px 20px',
                fontWeight: '500',
                border: '1px solid rgba(108, 117, 125, 0.2)'
              }}
            >
              Fechar
            </Button>
          </Modal.Footer>
        </Modal>

        {/* ✅ Drawer Padrão de Detalhes do Responsável */}
        <Drawer
          show={showDetailsDrawer}
          onHide={() => {
            setShowDetailsDrawer(false);
            setSelectedResponsible(null);
            setDocuments([]);
          }}
          title="Detalhes do Responsável"
          icon="mdi:account-details"
          size="xl"
          footer={
            <div className="d-flex gap-2 justify-content-end w-100">
              <Drawer.Button
                variant="secondary"
                onClick={() => {
                  setShowDetailsDrawer(false);
                  setSelectedResponsible(null);
                  setDocuments([]);
                }}
              >
                Fechar
              </Drawer.Button>
            </div>
          }
        >
            {selectedResponsible ? (
              <>
                {/* Status Badge */}
                <Drawer.Section>
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <Drawer.Badge
                      variant={
                        selectedResponsible.status === 'approved' ? 'success' :
                        selectedResponsible.status === 'rejected' ? 'danger' : 'warning'
                      }
                    >
                      {selectedResponsible.status === 'approved' ? 'Aprovado' :
                       selectedResponsible.status === 'rejected' ? 'Rejeitado' : 'Pendente'}
                    </Drawer.Badge>
                    <span style={{ fontSize: '12px', color: '#6c757d' }}>
                      Criado em {formatDate(selectedResponsible.created_at)}
                    </span>
                  </div>
                </Drawer.Section>
                <Drawer.Divider />

                {/* 🏢 Dados da Empresa/Pessoa */}
                <Drawer.Section
                  title={(() => {
                    const cleanDoc = (selectedResponsible.responsible_cpf || '').replace(/\D/g, '');
                    return cleanDoc.length === 14 ? 'Dados da Empresa' : 'Dados Pessoais';
                  })()}
                  icon="mdi:office-building"
                >
                  {(() => {
                    const cleanDoc = (selectedResponsible.responsible_cpf || '').replace(/\D/g, '');
                    return cleanDoc.length === 14;
                  })() && (
                    <>
                      <Drawer.Row 
                        label="Razão Social" 
                        value={selectedResponsible.company_name || 'Não informado'} 
                      />
                      <Drawer.Row 
                        label="Nome Fantasia" 
                        value={selectedResponsible.trading_name || 'Não informado'} 
                      />
                      <Drawer.Row 
                        label="CNPJ" 
                        value={selectedResponsible.responsible_cpf ? ResponsiblesService.formatCNPJ(selectedResponsible.responsible_cpf) : 'Não informado'} 
                      />
                      {selectedResponsible.state_registration && (
                        <Drawer.Row 
                          label="Inscrição Estadual" 
                          value={selectedResponsible.state_registration} 
                        />
                      )}
                      {selectedResponsible.founding_date && (
                        <Drawer.Row 
                          label="Data de Fundação" 
                          value={new Date(selectedResponsible.founding_date).toLocaleDateString('pt-BR')} 
                        />
                      )}
                      {selectedResponsible.site_url && (
                        <Drawer.Row 
                          label="Site da Empresa" 
                          value={
                            <a href={selectedResponsible.site_url} target="_blank" rel="noopener noreferrer">
                              {selectedResponsible.site_url}
                            </a>
                          } 
                        />
                      )}
                      {selectedResponsible.annual_revenue && (
                        <Drawer.Row 
                          label="Faturamento Anual" 
                          value={`R$ ${(selectedResponsible.annual_revenue / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`} 
                        />
                      )}
                    </>
                  )}
                  {selectedResponsible.responsible_phone && (
                    <Drawer.Row 
                      label="Telefone" 
                      value={ResponsiblesService.formatPhone(selectedResponsible.responsible_phone)} 
                    />
                  )}
                </Drawer.Section>
                <Drawer.Divider />

                {/* 👤 Dados do Sócio/Responsável */}
                <Drawer.Section
                  title={(() => {
                    const cleanDoc = (selectedResponsible.responsible_cpf || '').replace(/\D/g, '');
                    return cleanDoc.length === 14 ? 'Dados do Sócio Administrador' : 'Dados do Responsável';
                  })()}
                  icon="mdi:account-tie"
                >
                  <Drawer.Row 
                    label="Nome Completo" 
                    value={selectedResponsible.admin_name || 'Não informado'} 
                  />
                  <Drawer.Row 
                    label="CPF" 
                    value={selectedResponsible.admin_cpf ? ResponsiblesService.formatCPF(selectedResponsible.admin_cpf) : 'Não informado'} 
                  />
                  {selectedResponsible.admin_birth_date && (
                    <Drawer.Row 
                      label="Data de Nascimento" 
                      value={new Date(selectedResponsible.admin_birth_date).toLocaleDateString('pt-BR')} 
                    />
                  )}
                  {selectedResponsible.admin_mother_name && (
                    <Drawer.Row 
                      label="Nome da Mãe" 
                      value={selectedResponsible.admin_mother_name} 
                    />
                  )}
                  {selectedResponsible.monthly_income && (
                    <Drawer.Row 
                      label="Renda Mensal" 
                      value={`R$ ${(selectedResponsible.monthly_income / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`} 
                    />
                  )}
                  {selectedResponsible.professional_occupation && (
                    <Drawer.Row 
                      label="Ocupação Profissional" 
                      value={selectedResponsible.professional_occupation} 
                    />
                  )}
                </Drawer.Section>
                <Drawer.Divider />

                {/* 📍 Endereço */}
                <Drawer.Section title="Endereço" icon="mdi:map-marker">
                  {selectedResponsible.address_cep && (
                    <Drawer.Row label="CEP" value={selectedResponsible.address_cep} />
                  )}
                  {selectedResponsible.address_street && (
                    <Drawer.Row 
                      label="Logradouro" 
                      value={`${selectedResponsible.address_street}, ${selectedResponsible.address_number || 'S/N'}`} 
                    />
                  )}
                  {selectedResponsible.address_complement && (
                    <Drawer.Row label="Complemento" value={selectedResponsible.address_complement} />
                  )}
                  {selectedResponsible.address_neighborhood && (
                    <Drawer.Row label="Bairro" value={selectedResponsible.address_neighborhood} />
                  )}
                  {selectedResponsible.address_city && selectedResponsible.address_state && (
                    <Drawer.Row 
                      label="Cidade/Estado" 
                      value={`${selectedResponsible.address_city}/${selectedResponsible.address_state}`} 
                    />
                  )}
                  {selectedResponsible.address_reference && (
                    <Drawer.Row 
                      label="Ponto de Referência" 
                      value={selectedResponsible.address_reference} 
                    />
                  )}
                </Drawer.Section>
                <Drawer.Divider />

                {/* ❌ Motivo da Rejeição (se rejeitado) */}
                {selectedResponsible.rejection_reason && (
                  <>
                    <Drawer.Section title="Motivo da Rejeição" icon="mdi:alert-circle">
                      <Drawer.InfoCard variant="danger">
                        <p style={{ margin: 0, fontSize: '14px' }}>{selectedResponsible.rejection_reason}</p>
                      </Drawer.InfoCard>
                    </Drawer.Section>
                    <Drawer.Divider />
                  </>
                )}

                {/* 📄 Documentos */}
                <Drawer.Section 
                  title={`Documentos do Responsável (${documents.length})`} 
                  icon="mdi:file-document-multiple"
                >
                  {documents.length === 0 ? (
                    <Drawer.EmptyState 
                      icon="mdi:file-remove" 
                      text="Nenhum documento foi enviado ainda."
                    />
                  ) : (
                    <div>
                      {documents.map((doc, index) => (
                        <div 
                          key={index} 
                          style={{ 
                            border: '1px solid #e9ecef', 
                            borderRadius: '8px', 
                            padding: '16px',
                            marginBottom: '12px',
                            backgroundColor: '#ffffff',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                          }}
                        >
                          {/* Card do Documento */}
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div className="flex-grow-1">
                              <div style={{ 
                                fontSize: '14px', 
                                fontWeight: '600', 
                                color: '#212529', 
                                textTransform: 'uppercase', 
                                marginBottom: '8px' 
                              }}>
                                {doc.document_type || 'Documento'}
                              </div>
                              <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px' }}>
                                <Icon icon="mdi:file" style={{ fontSize: '12px', marginRight: '4px' }} />
                                {doc.file_name || 'Sem nome'}
                              </div>
                              <div style={{ fontSize: '11px', color: '#6c757d' }}>
                                <Icon icon="mdi:calendar" style={{ fontSize: '11px', marginRight: '4px' }} />
                                Enviado em: {formatDate(doc.uploaded_at || doc.created_at)}
                              </div>
                            </div>
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => {
                                const docUrl = getDocumentUrl(doc);
                                if (docUrl) {
                                  window.open(docUrl, '_blank');
                                }
                              }}
                              style={{ 
                                padding: '8px 16px', 
                                fontSize: '13px', 
                                marginLeft: '16px', 
                                whiteSpace: 'nowrap' 
                              }}
                            >
                              <Icon icon="mdi:eye" style={{ fontSize: '16px', marginRight: '6px' }} />
                              Visualizar
                            </Button>
                          </div>

                          {/* ✅ Preview de Imagem (se for imagem) */}
                          {isImageFile(doc.file_name) && getDocumentUrl(doc) && (
                            <div className="text-center mt-3">
                              <img 
                                src={getDocumentUrl(doc)} 
                                alt={doc.document_type}
                                style={{ 
                                  maxWidth: '100%', 
                                  maxHeight: '300px', 
                                  borderRadius: '8px',
                                  objectFit: 'contain',
                                  border: '1px solid #e9ecef'
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </Drawer.Section>
              </>
            ) : (
              <Drawer.EmptyState 
                icon="mdi:alert" 
                text="Não foi possível carregar os detalhes do responsável."
              />
            )}
        </Drawer>
      </MasterLayout>
  );
};

export default ResponsiblesPage;